import type { CSSProperties, ReactNode } from 'react';

import { DragHandle } from '@/lib/base/foundations/drag-handle';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';
import { cx } from '@/utils/cx';

// ── Shared constants ───────────────────────────────────────────────────────

const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand';

// ── Public interface ───────────────────────────────────────────────────────

export interface DraggableItemProps {
  /** Content (rendered to the right of the drag handle for card/row, after the grip for chip). */
  children: ReactNode;
  /**
   * Visual variant:
   * - `'card'` (default) — full-width bordered card with a distinct handle
   *   strip on the left. Only the handle initiates drag.
   * - `'chip'` — inline fit-content pill. The entire chip is the drag
   *   target; the grip icon is a leading visual affordance only.
   * - `'row'` — full-width borderless row for seamless table-like stacking.
   *   Only the handle initiates drag. Rows should be wrapped in a container
   *   with its own border and rounding.
   */
  variant?: 'card' | 'chip' | 'row';
  /** Ref callback from `useSortable().setNodeRef`. */
  nodeRef?: (node: HTMLElement | null) => void;
  /** Inline transform/transition style from `useSortable()`. */
  style?: CSSProperties;
  /**
   * Props to spread on the drag surface — typically the merged
   * `{ ...listeners, ...attributes }` from `useSortable()` / `useDraggable()`.
   *
   * - `variant='card'` / `'row'`: spread on the handle button only.
   * - `variant='chip'`: spread on the outer wrapper (whole chip is draggable).
   */
  dragHandleProps?: Record<string, unknown>;
  /** Whether the item is currently being dragged (reduces opacity). */
  isDragging?: boolean;
  /** Additional className on the outer wrapper. */
  className?: string;
  /**
   * Additional className on the drag handle.
   * Only used for `variant='card'` / `'row'` (e.g. `'hidden md:flex'` for responsive).
   */
  handleClassName?: string;
  /** Accessible label for the drag handle. Defaults to "Drag to reorder". */
  dragHandleLabel?: string;
}

/**
 * DraggableItem — a layout primitive for draggable items in three flavours.
 *
 * **Card variant** (default) — a bordered card with a full-height
 * drag-handle strip on the left edge. Only the handle initiates drag.
 *
 * ```
 * ┌──┬─────────────────────────────────────┐
 * │  │                                     │
 * │⠿│  children                            │
 * │  │                                     │
 * └──┴─────────────────────────────────────┘
 * ```
 *
 * **Chip variant** — a compact, inline pill where the entire surface is
 * the drag target. The grip icon is a leading visual affordance.
 *
 * ```
 * ┌──────────────┐
 * │ ⠿ column_name│
 * └──────────────┘
 * ```
 *
 * **Row variant** — a full-width borderless row with a handle strip on
 * the left. Stack inside a bordered container for seamless table rows.
 *
 * ```
 * ──┬─────────────────────────────────────
 * ⠿│  row content
 * ──┴─────────────────────────────────────
 * ```
 *
 * This is a layout primitive — it does NOT call `useSortable` or
 * `useDraggable`. The consumer hooks up whichever drag library they
 * use and passes the handle/surface props through.
 */
export function DraggableItem({
  children,
  variant = 'card',
  nodeRef,
  style,
  dragHandleProps,
  isDragging,
  className,
  handleClassName,
  dragHandleLabel = 'Drag to reorder',
}: DraggableItemProps) {
  if (variant === 'chip') {
    return (
      <div
        ref={nodeRef}
        style={style}
        role='button'
        tabIndex={0}
        aria-label={dragHandleLabel}
        className={cx(
          'inline-flex items-center gap-1 rounded-md border border-border-secondary',
          'bg-bg-secondary text-fg-secondary',
          'px-2 py-1 text-sm',
          'cursor-grab touch-none active:cursor-grabbing',
          FOCUS_RING,
          isDragging && 'opacity-40 shadow-lg',
          className
        )}
        {...dragHandleProps}
      >
        <GripVertical className='size-4 shrink-0 text-fg-quaternary' />
        {children}
      </div>
    );
  }

  // Card and row variants share the handle-strip layout
  const isRow = variant === 'row';

  return (
    <div
      ref={nodeRef}
      style={style}
      className={cx(
        'flex overflow-hidden bg-bg-primary transition-shadow duration-100',
        !isRow && 'rounded-lg border border-border-secondary',
        isRow && 'border-b border-border-secondary last:border-b-0',
        isDragging && 'opacity-40 shadow-lg',
        className
      )}
    >
      {/* Full-height drag handle strip */}
      <DragHandle
        dragHandleProps={dragHandleProps}
        label={dragHandleLabel}
        className={cx(
          'flex items-center justify-center w-7 flex-shrink-0 self-stretch',
          'bg-bg-secondary',
          !isRow && 'rounded-l-lg',
          handleClassName
        )}
      />

      {/* Content area */}
      <div className='flex-1 min-w-0'>{children}</div>
    </div>
  );
}

// ── Backward-compatible alias ──────────────────────────────────────────────

/** @deprecated Use `DraggableItem` instead. */
export type DraggableCardProps = DraggableItemProps;

/** @deprecated Use `DraggableItem` instead. */
export const DraggableCard = DraggableItem;
